import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import Logo from '@/components/Logo';
import { ArrowLeft } from 'lucide-react';
import SEO from '@/components/SEO';

export default function PrivacyPolicy() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      <SEO
        title="Privacy Policy"
        description="How Snap Windows collects, uses, and protects your personal information when you request an impact window and door quote."
        path="/privacy-policy"
        noindex
      />
      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur border-b border-[hsl(var(--border))]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 flex items-center justify-between h-14">
          <Logo variant="light" size="small" />
          <Button variant="ghost" size="sm" onClick={() => navigate('/')} className="text-[var(--brand-slate-600)] text-xs">
            <ArrowLeft className="mr-1 h-4 w-4" />
            Back to Home
          </Button>
        </div>
      </nav>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-[var(--brand-slate-900)] mb-3">Privacy Policy</h1>
        <p className="text-sm text-[var(--brand-slate-600)] mb-8">Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>

        <Card className="rounded-2xl shadow-[var(--shadow-soft)] mb-6">
          <CardContent className="p-6 sm:p-8 space-y-6">
            <section>
              <h2 className="font-heading text-xl font-semibold text-[var(--brand-slate-900)] mb-3">Introduction</h2>
              <p className="text-sm text-[var(--brand-slate-600)] leading-relaxed">
                Snap Windows ("we," "us," or "our") operates snap-windows.com. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website and use our quote request services.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl font-semibold text-[var(--brand-slate-900)] mb-3">Information We Collect</h2>
              <div className="space-y-4 text-sm text-[var(--brand-slate-600)]">
                <div>
                  <p className="font-semibold text-[var(--brand-slate-900)] mb-2">Personal Information</p>
                  <p className="leading-relaxed mb-2">When you submit a quote request, we collect:</p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Full name</li>
                    <li>Email address</li>
                    <li>Phone number</li>
                    <li>Property address and ZIP code</li>
                    <li>City and county</li>
                  </ul>
                </div>

                <div>
                  <p className="font-semibold text-[var(--brand-slate-900)] mb-2">Project Information</p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Property type and project scope</li>
                    <li>Number of windows and doors</li>
                    <li>Timeline and budget preferences</li>
                    <li>Additional project notes</li>
                  </ul>
                </div>

                <div>
                  <p className="font-semibold text-[var(--brand-slate-900)] mb-2">Photos and Media</p>
                  <p className="leading-relaxed">
                    You may upload photos of your windows and doors. These images are stored securely and used solely for providing accurate quote estimates.
                  </p>
                </div>

                <div>
                  <p className="font-semibold text-[var(--brand-slate-900)] mb-2">Usage Data</p>
                  <p className="leading-relaxed">
                    We automatically collect certain information when you visit our website, including IP address, browser type, pages visited, time spent on pages, and referring website.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="font-heading text-xl font-semibold text-[var(--brand-slate-900)] mb-3">How We Use Your Information</h2>
              <div className="space-y-2 text-sm text-[var(--brand-slate-600)]">
                <p className="leading-relaxed">We use the information we collect to:</p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Prepare and deliver customized quotes for your project</li>
                  <li>Communicate with you via email, SMS, or phone regarding your quote</li>
                  <li>Schedule consultations, site visits, or installations</li>
                  <li>Process and manage your project from quote to completion</li>
                  <li>Send confirmations, updates, and important project information</li>
                  <li>Improve our website, services, and customer experience</li>
                  <li>Comply with legal obligations and industry regulations</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="font-heading text-xl font-semibold text-[var(--brand-slate-900)] mb-3">Communication Consent</h2>
              <div className="space-y-3 text-sm text-[var(--brand-slate-600)]">
                <div className="bg-[var(--brand-ocean-50)] rounded-lg p-4 border border-[var(--brand-ocean-200)]">
                  <p className="font-semibold text-[var(--brand-slate-900)] mb-2">SMS/Text Messaging</p>
                  <p className="leading-relaxed">
                    By providing your phone number, you consent to receive text messages (SMS) from Snap Windows regarding your quote request, project updates, and appointment confirmations. Message and data rates may apply. You may opt out at any time by replying "STOP" to any text message.
                  </p>
                </div>

                <div className="bg-[var(--brand-ocean-50)] rounded-lg p-4 border border-[var(--brand-ocean-200)]">
                  <p className="font-semibold text-[var(--brand-slate-900)] mb-2">Email Communication</p>
                  <p className="leading-relaxed">
                    By providing your email address, you consent to receive email communications from Snap Windows, including quote confirmations, project updates, and follow-up messages. You may unsubscribe from promotional emails at any time using the unsubscribe link in each email.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="font-heading text-xl font-semibold text-[var(--brand-slate-900)] mb-3">Photo Upload and Usage</h2>
              <div className="space-y-2 text-sm text-[var(--brand-slate-600)]">
                <p className="leading-relaxed">
                  When you upload photos of your windows and doors:
                </p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Photos are stored securely on our servers</li>
                  <li>We use them exclusively to prepare accurate quotes and project assessments</li>
                  <li>Photos are shared with our team of estimators and project managers</li>
                  <li>We do not sell, share, or use your photos for marketing without explicit consent</li>
                  <li>You retain ownership of all uploaded photos</li>
                  <li>You may request deletion of your photos at any time by contacting us</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="font-heading text-xl font-semibold text-[var(--brand-slate-900)] mb-3">Information Sharing and Disclosure</h2>
              <div className="space-y-3 text-sm text-[var(--brand-slate-600)]">
                <p className="leading-relaxed">
                  We do not sell your personal information. We may share your information with:
                </p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li><strong>Service Providers:</strong> Third-party companies that assist with CRM, email, SMS, and project management (e.g., GoHighLevel, cloud storage providers)</li>
                  <li><strong>Manufacturers & Suppliers:</strong> To obtain accurate product pricing and availability for your quote</li>
                  <li><strong>Permitting Authorities:</strong> As required for building permits and inspections</li>
                  <li><strong>Legal Compliance:</strong> If required by law, regulation, legal process, or governmental request</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="font-heading text-xl font-semibold text-[var(--brand-slate-900)] mb-3">Data Security</h2>
              <p className="text-sm text-[var(--brand-slate-600)] leading-relaxed">
                We implement industry-standard security measures to protect your personal information, including secure servers, encryption, and access controls. However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl font-semibold text-[var(--brand-slate-900)] mb-3">Cookies and Tracking</h2>
              <div className="space-y-2 text-sm text-[var(--brand-slate-600)]">
                <p className="leading-relaxed">
                  We use cookies and similar tracking technologies to enhance your experience:
                </p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Session cookies to maintain your quiz progress</li>
                  <li>Analytics cookies to understand website usage (e.g., Google Analytics)</li>
                  <li>Marketing pixels (e.g., Facebook Pixel) to measure ad performance</li>
                </ul>
                <p className="leading-relaxed mt-2">
                  You can control cookies through your browser settings, but disabling cookies may limit website functionality.
                </p>
              </div>
            </section>

            <section>
              <h2 className="font-heading text-xl font-semibold text-[var(--brand-slate-900)] mb-3">Your Rights</h2>
              <div className="space-y-2 text-sm text-[var(--brand-slate-600)]">
                <p className="leading-relaxed">You have the right to:</p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Access the personal information we hold about you</li>
                  <li>Request correction of inaccurate information</li>
                  <li>Request deletion of your information (subject to legal retention requirements)</li>
                  <li>Opt out of marketing communications</li>
                  <li>Withdraw consent for SMS/email communication at any time</li>
                </ul>
                <p className="leading-relaxed mt-3">
                  To exercise these rights, contact us at <a href="mailto:quotes@snap-windows.com" className="text-[var(--brand-ocean-700)] hover:underline">quotes@snap-windows.com</a>
                </p>
              </div>
            </section>

            <section>
              <h2 className="font-heading text-xl font-semibold text-[var(--brand-slate-900)] mb-3">Children's Privacy</h2>
              <p className="text-sm text-[var(--brand-slate-600)] leading-relaxed">
                Our services are not directed to individuals under 18 years of age. We do not knowingly collect personal information from children.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl font-semibold text-[var(--brand-slate-900)] mb-3">Changes to This Privacy Policy</h2>
              <p className="text-sm text-[var(--brand-slate-600)] leading-relaxed">
                We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated "Last Updated" date. We encourage you to review this policy periodically.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl font-semibold text-[var(--brand-slate-900)] mb-3">Contact Us</h2>
              <div className="text-sm text-[var(--brand-slate-600)] space-y-1">
                <p className="leading-relaxed">If you have questions about this Privacy Policy, please contact us:</p>
                <p className="font-semibold text-[var(--brand-slate-900)] mt-3">Snap Windows</p>
                <p>Licensed General Contractor: CGC1524851</p>
                <p>Email: <a href="mailto:quotes@snap-windows.com" className="text-[var(--brand-ocean-700)] hover:underline">quotes@snap-windows.com</a></p>
                <p>Serving: Miami-Dade, Broward, and Palm Beach Counties</p>
              </div>
            </section>
          </CardContent>
        </Card>

        <div className="text-center">
          <Button onClick={() => navigate('/')} className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] rounded-xl">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Home
          </Button>
        </div>
      </div>
    </div>
  );
}
